<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useToast } from '@ai/ui-components/toast'
import { useUserInviteForm } from '@/composables/users/useUserInviteForm'

const MAX_CSV_ROWS = 100
const MAX_CSV_SIZE = 1 * 1024 * 1024 // 1 MB

const { t } = useI18n()
const toast = useToast()
const open = defineModel<boolean>('open', { default: false })
const emit = defineEmits<{ userInvited: [] }>()

const users = ref<Record<string, string>[]>([])
const csvFile = ref<File | null>(null)
const csvError = ref(false)
const uploaderRef = ref()

const hasUnsavedChanges = computed(() => users.value.length > 0)

const resetForm = () => {
  users.value = []
  csvFile.value = null
  csvError.value = false
}

const handleSave = async () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  uploaderRef.value?.validate?.()
  if (!csvFile.value) return
  await handleSubmit()
}

const {
  localizedRoleOptions,
  showCancelConfirmation,
  rowErrors,
  isSubmitting,
  panelOpen,
  handleCancel,
  confirmDiscard,
  keepEditing,
  handleFieldBlur,
  handleSubmit,
  markAllSubmitted,
} = useUserInviteForm({
  users,
  open,
  hasUnsavedChanges,
  onUserInvited: () => {
    emit('userInvited')
  },
  onReset: resetForm,
})

const normalizeRole = (raw: string): string => {
  const lower = raw.trim().toLowerCase()
  if (lower === 'administrator' || lower === 'admin') return 'admin'
  return 'supervisor'
}

const parseCsv = (content: string): { email: string; role: string }[] => {
  const lines = content
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)

  const startIndex = /^email[,;]?/i.test(lines[0]) ? 1 : 0
  const dataLines = lines.slice(startIndex)

  if (dataLines.length > MAX_CSV_ROWS) {
    throw new Error('TOO_MANY_ROWS')
  }

  return dataLines.map((line) => {
    const [email = '', role = ''] = line.split(/[,;]/).map((s) => s.trim())
    return { email, role: normalizeRole(role) }
  })
}

watch(csvFile, (file) => {
  if (!file) return

  if (file.size > MAX_CSV_SIZE) {
    csvError.value = true
    toast.error(t('users_import.csv_too_large'), { title: t('error_occurred') })
    return
  }

  const reader = new FileReader()

  reader.onload = (event) => {
    try {
      const content = event.target?.result as string
      const parsed = parseCsv(content)

      if (!parsed.length) {
        throw new Error('Empty CSV')
      }

      users.value = parsed
      markAllSubmitted()
      csvError.value = false
    } catch (e) {
      if (e instanceof Error && e.message === 'TOO_MANY_ROWS') {
        csvError.value = true
        toast.error(t('users_import.csv_too_many_rows'), { title: t('error_occurred') })
      } else {
        csvError.value = true
        open.value = false
        toast.error(t('users_import.csv_import_error'), { title: t('error_occurred') })
      }
    }
  }

  reader.onerror = () => {
    csvError.value = true
    open.value = false
    toast.error(t('users_import.csv_import_error'), { title: t('error_occurred') })
  }

  reader.readAsText(file)
})
</script>

<template>
  <ORightPanel v-model:open="panelOpen" :title="t('users_import.import_title')">
    <template #header>
      <div class="flex flex-col gap-s">
        <p class="text-neutral-1 typography-body-2-long" data-testid="csv-import-helper">
          {{ t('users_import.csv_helper') }}
        </p>
        <OFileUploader
          ref="uploaderRef"
          id="csv-user-import"
          v-model="csvFile"
          accept=".csv"
          :max-files="1"
          :maxSizeFile="1"
          :strict-file-limit="false"
          :error="csvError"
          :can-remove-file="false"
          data-testid="csv-user-file-uploader"
        />
      </div>
    </template>

    <template #default>
      <div
        v-if="users.length"
        class="import-user-panel flex h-full flex-col space-y-4 pb-2 text-neutral-1"
      >
        <div class="flex flex-col">
          <span class="text-neutral-1 typography-body-1-medium">{{
            t('users_import.review_title')
          }}</span>
          <span class="typography-caption-1">{{
            t('users_import.contacts_detected', { count: users.length })
          }}</span>
        </div>
        <OMultiInput
          v-model="users"
          :fields="['email', 'role']"
          :placeholders="[t('enter_team_member_email_address_placeholder'), t('role_placeholder')]"
          :field-types="['input', 'dropdown']"
          :field-input-types="['email']"
          :field-options="[[], localizedRoleOptions]"
          :row-errors="rowErrors"
          :add-label="t('users_import.add_new')"
          :add-button-size="'S'"
          :field-defaults="['', 'supervisor']"
          :delete-label="t('users_import.delete')"
          dataTestid="import-user-multi-input"
          class="min-h-0 flex-1"
          @fieldBlur="handleFieldBlur"
        />
      </div>
    </template>

    <template #footer>
      <OButton
        :label="t('cancel')"
        variant="secondary"
        size="L"
        color="neutral"
        @mousedown.prevent
        @click="handleCancel"
        dataTestid="import-user-cancel"
      />
      <OButton
        :label="t('save')"
        size="L"
        :loading="isSubmitting"
        @click="handleSave"
        dataTestid="import-user-confirm"
        backgroundColor="success"
      />
    </template>
  </ORightPanel>

  <OModal
    v-model:open="showCancelConfirmation"
    :title="t('unsaved_changes')"
    :primary-action-title="t('discard_changes')"
    :secondary-action-title="t('continue')"
    :primary-action-promise="confirmDiscard"
    :secondary-action-promise="keepEditing"
    primary-action-enabled
    secondary-action-enabled
    danger
  >
    <template #content>
      <span class="text-neutral-1">{{ t('unsaved_changes_warning') }}</span>
    </template>
  </OModal>
</template>

<style scoped>
.import-user-panel :deep(.max-h-52) {
  max-height: none;
}

.import-user-panel :deep(.px-1) {
  padding-left: 0;
  overflow: visible;
}
</style>
